/**
 * The Danish clock (user, 15/10/2026: "it should always be Danish time"):
 * a container's start, end and pinned drop times are Danish clock minutes,
 * fixed all year; the country's minute is worked out per RUN DATE, exactly
 * — the moment Copenhagen shows that time, mapped into the country's run
 * day [00:00, 24:00). Never a delta "at noon": on Australia's run date
 * 25/10 local noon is Denmark's own clock change. A Danish time that
 * happens twice takes the later; one that never happens moves to the next
 * valid minute (`wallTimeToUtc`'s contract).
 * For Media Devoted | Created By Ciro Autiero
 */
import { addDaysIso, daysBetweenIso, localDateInTz, tzOffsetMin, wallMinutesInTz, wallTimeToUtc } from "../wall-clock";

export const DANISH_TZ = "Europe/Copenhagen";

export const mod1440 = (x: number): number => ((x % 1440) + 1440) % 1440;

/** The country's day the conversion is for; tz null = no conversion (no timezone in the catalog). */
export interface CountryDay {
  tz: string | null;
  /** The country's run date "YYYY-MM-DD". */
  runDate: string;
}

/** Country offset − Danish offset at `at`, in minutes (Sydney in summer: +600). */
export function cphDeltaMin(tz: string, at: Date): number {
  return tzOffsetMin(tz, at) - tzOffsetMin(DANISH_TZ, at);
}

/** Wall-clock minutes of `instant` counted from the country's run-day midnight (may be < 0 or ≥ 1440). */
export function runDayMinute(instant: Date, runDate: string, tz: string): number {
  return daysBetweenIso(runDate, localDateInTz(instant, tz)) * 1440 + wallMinutesInTz(instant, tz);
}

/** The country's minute on the run day for a Danish clock time (≥ 1440 keeps "+1 local day"). */
export function dkToLocal(dkMin: number, day: CountryDay): number {
  if (!day.tz) return dkMin;
  const tod = mod1440(dkMin);
  const noon = wallTimeToUtc({ dateIso: day.runDate, minutesFromMidnight: 720, tz: day.tz });
  const approx = mod1440(tod + cphDeltaMin(day.tz, noon));
  return (nearestRunDayMinute(tod, approx, { tz: day.tz, runDate: day.runDate }) ?? approx) + (dkMin >= 1440 ? 1440 : 0);
}

/** The instant Copenhagen shows `tod` closest to `approx` inside the run day, as a run-day minute. */
function nearestRunDayMinute(tod: number, approx: number, day: { tz: string; runDate: string }): number | null {
  const around = wallTimeToUtc({ dateIso: day.runDate, minutesFromMidnight: approx, tz: day.tz });
  const dkDate = localDateInTz(around, DANISH_TZ);
  const inDay = [-1, 0, 1]
    .map((k) => runDayMinute(wallTimeToUtc({ dateIso: addDaysIso(dkDate, k), minutesFromMidnight: tod, tz: DANISH_TZ }), day.runDate, day.tz))
    .filter((m) => m >= 0 && m < 1440);
  if (inDay.length === 0) return null;
  return inDay.reduce((best, m) => (Math.abs(m - approx) < Math.abs(best - approx) ? m : best));
}

/** The Danish clock time of a country minute on the run day; dayShift −1 = the Danish date is the day before. */
export function localToDk(localMin: number, day: CountryDay): { dkMin: number; dayShift: number } {
  if (!day.tz) return { dkMin: localMin, dayShift: 0 };
  const instant = localInstant(localMin, { tz: day.tz, runDate: day.runDate });
  const localDate = addDaysIso(day.runDate, Math.floor(localMin / 1440));
  return { dkMin: wallMinutesInTz(instant, DANISH_TZ), dayShift: daysBetweenIso(localDate, localDateInTz(instant, DANISH_TZ)) };
}

/** The instant of a run-day minute (≥ 1440 = the next calendar day). */
export function localInstant(localMin: number, day: { tz: string; runDate: string }): Date {
  return wallTimeToUtc({ dateIso: addDaysIso(day.runDate, Math.floor(localMin / 1440)), minutesFromMidnight: mod1440(localMin), tz: day.tz });
}
